import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Users, Crown, Skull } from 'lucide-react';
import { SectionHeading } from '../components/ui';
import { PricingCard, PricingPlan } from '../components/PricingCard';
const DRAFT_DAY: PricingPlan = {
  id: 'draft',
  title: 'Draft Day Rental',
  price: '$19.99',
  period: 'one time',
  badge: 'ONE NIGHT ONLY',
  badgeColor: 'lime',
  borderClass: 'border-lime',
  glowClass: 'hover:shadow-[0_0_30px_rgba(200,255,0,0.2)]',
  description: 'No commitment. No season subscription. Just pure chaos for one draft night. Live commentary as picks come in. Characters react in real time. Your commish group chat will never be the same.',
  ctaText: 'Rent Draft Night',
  ctaClass: 'bg-transparent border-2 border-lime text-lime hover:bg-lime hover:text-charcoal'
};
const PRICING_TIERS: PricingPlan[] = [{
  id: 'schoolyard',
  title: 'The Schoolyard',
  price: '$4.99',
  period: 'person/season',
  borderClass: 'border-blue-500',
  glowClass: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.2)]',
  description: 'Clean comedy. No swearing. Family friendly roasts.',
  features: ['Up to 3 commentary clips per week', 'Perfect for office leagues and family competitions'],
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-blue-500 text-white hover:bg-blue-600',
  fundraisingNote: true
}, {
  id: 'lockerroom',
  title: 'The Locker Room',
  price: '$9.99',
  period: 'person/season',
  badge: '⭐ MOST POPULAR',
  badgeColor: 'orange',
  borderClass: 'border-orange',
  glowClass: 'shadow-[0_0_20px_rgba(255,107,0,0.15)] hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] scale-105 z-10',
  description: 'Real talk. Unfiltered commentary. The full roast experience.',
  features: ['Up to 6 commentary clips per week', 'Characters: Vinny, Dale, Marisol, Giuseppe, Daniel, Gary, Kevin Chen, Marcus, Ricky Rotardo'],
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-orange text-white hover:bg-orange/80',
  popular: true,
  fundraisingNote: true
}, {
  id: 'goodoldboys',
  title: 'Good Old Boys',
  price: '$14.99',
  period: 'person/season',
  borderClass: 'border-green-600',
  glowClass: 'hover:shadow-[0_0_20px_rgba(22,163,74,0.2)]',
  description: 'Premium roasts. Smack talk intel. Owner-submitted dirt used in commentary.',
  features: ['Up to 10 commentary clips per week', 'Includes SMS audio clip delivery direct to your phone'],
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-green-600 text-white hover:bg-green-700',
  fundraisingNote: true
}, {
  id: 'theroast',
  title: 'The Roast',
  price: '$19.99',
  period: 'person/season',
  badge: '🔥 NO MERCY',
  badgeColor: 'crimson',
  borderClass: 'border-crimson',
  glowClass: 'shadow-[0_0_30px_rgba(220,38,38,0.3)] hover:shadow-[0_0_50px_rgba(220,38,38,0.5)]',
  description: 'No limits. No mercy. Unlimited clips. Full character rotation.',
  features: ['Official notification calls — drug tests, HR violations, wellness checks', 'James Coleman journalist intel calls', 'Animated shareable clips. Everything. Every week. All season.'],
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-crimson text-white hover:bg-red-700',
  fundraisingNote: true
}];
const FAQS = [{
  q: 'How much does it cost per league?',
  a: "Pricing is per person, per season. A 10-person league on The Locker Room tier costs $9.99 per person — that's less than $1 per week of premium roast content."
}, {
  q: "How does the 'Loser Pays' mechanic work?",
  a: "If you select Loser Pays, we authorize everyone's card for the per-person amount at the start of the season. At the end of the season, the person in last place gets charged the full amount for the entire league. Everyone else pays nothing. It's the most satisfying way to lose."
}, {
  q: 'Where does the fundraising money go?',
  a: '20% of every subscription is allocated to school fundraising programs through our School Spirit partnerships. The funnier your league gets, the more schools benefit.'
}, {
  q: 'Does this work with ESPN or Yahoo?',
  a: 'Currently, we only integrate directly with Sleeper for automated real-time reactions. We are working on ESPN and Yahoo integrations for next season.'
}];
export const PricingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  return <div className="w-full pt-20" data-id="element-730">
      {/* HEADER */}
      <section className="py-20 bg-charcoal text-center border-b border-white/5" data-id="element-731">
        <div className="container mx-auto px-4" data-id="element-732">
          <SectionHeading title="SIMPLE PRICING. HILARIOUS RETURNS." subtitle="Pick your league's personality. Pay however you want. A portion of every subscription goes to school fundraising programs." className="mb-0 mx-auto flex flex-col items-center max-w-4xl" data-id="element-733" />
        </div>
      </section>

      {/* DRAFT DAY RENTAL */}
      <section className="py-16 bg-dark" data-id="element-734">
        <div className="container mx-auto px-4" data-id="element-735">
          <div className="max-w-4xl mx-auto" data-id="element-736">
            <PricingCard plan={DRAFT_DAY} data-id="element-737" />
          </div>
        </div>
      </section>

      {/* TIER CARDS */}
      <section className="py-20 bg-charcoal" data-id="element-738">
        <div className="container mx-auto px-4" data-id="element-739">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch max-w-[1400px] mx-auto" data-id="element-740">
            {PRICING_TIERS.map(plan => <PricingCard key={plan.id} plan={plan} data-id="element-741" />)}
          </div>
        </div>
      </section>

      {/* HOW DO YOU WANT TO PAY? */}
      <section className="py-24 bg-dark border-y border-white/5" data-id="element-742">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-743">
          <h2 className="text-4xl md:text-5xl font-black font-heading uppercase -skew-x-6 mb-12 text-center text-white" data-id="element-744">
            HOW DO YOU WANT TO PAY?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-745">
            <motion.div whileHover={{
            y: -5
          }} className="bg-charcoal p-8 rounded-2xl border border-white/10 text-center" data-id="element-746">
              <div className="w-16 h-16 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-747">
                <Users size={32} data-id="element-748" />
              </div>
              <h4 className="font-heading font-black text-2xl uppercase tracking-wide mb-4 text-white" data-id="element-749">
                EVERYONE PAYS
              </h4>
              <p className="text-slate" data-id="element-750">
                Each person in your league pays their own way. Split it up. No
                awkward conversations.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-charcoal p-8 rounded-2xl border border-gold/30 text-center shadow-[0_0_30px_rgba(245,197,24,0.1)]" data-id="element-751">
              <div className="w-16 h-16 bg-gold/20 text-gold rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-752">
                <Crown size={32} data-id="element-753" />
              </div>
              <h4 className="font-heading font-black text-2xl uppercase tracking-wide mb-4 text-gold" data-id="element-754">
                COMMISSIONER PAYS
              </h4>
              <p className="text-slate" data-id="element-755">
                Cover your whole league and save 15%. You bring the roasts, they
                bring the respect.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-charcoal p-8 rounded-2xl border border-crimson/30 text-center shadow-[0_0_30px_rgba(220,38,38,0.1)]" data-id="element-756">
              <div className="w-16 h-16 bg-crimson/20 text-crimson rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-757">
                <Skull size={32} data-id="element-758" />
              </div>
              <h4 className="font-heading font-black text-2xl uppercase tracking-wide mb-2 text-crimson" data-id="element-759">
                LOSER PAYS
              </h4>
              <p className="text-xs font-bold text-crimson uppercase tracking-wider mb-4" data-id="element-760">
                The most satisfying way to lose
              </p>
              <p className="text-slate" data-id="element-761">
                Last place funds next season's roasting for everyone. You finish
                last, you pay for your own humiliation. Most viral setup in
                fantasy sports.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FUNDRAISING BANNER */}
      <section className="py-20 bg-lime text-charcoal relative overflow-hidden" data-id="element-762">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" data-id="element-763"></div>
        <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl" data-id="element-764">
          <h2 className="text-5xl md:text-7xl font-black font-heading uppercase -skew-x-6 mb-6 tracking-tighter" data-id="element-765">
            GIVING BACK WITH EVERY ROAST
          </h2>
          <p className="text-2xl font-bold mb-6" data-id="element-766">
            20% of every subscription goes to school fundraising programs.
          </p>
          <p className="text-xl font-medium" data-id="element-767">
            The funnier your league gets, the more schools benefit. Roast
            somebody. Help a kid.
          </p>
        </div>
      </section>

      {/* AFFILIATE SECTION */}
      <section className="py-24 bg-dark border-b border-white/5" data-id="element-768">
        <div className="container mx-auto px-4 text-center max-w-4xl" data-id="element-769">
          <h2 className="text-5xl md:text-7xl font-black font-heading uppercase -skew-x-6 mb-6 text-white" data-id="element-770">
            MAKE MONEY RUNNING LEAGUES
          </h2>
          <p className="text-2xl font-bold mb-12 text-slate" data-id="element-771">
            Share your link. Every commissioner you refer pays you every season.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-772">
            <div className="bg-charcoal text-white p-8 rounded-2xl border-4 border-dark" data-id="element-773">
              <h3 className="font-heading font-black text-2xl text-lime mb-2" data-id="element-774">
                STANDARD
              </h3>
              <p className="text-4xl font-black font-mono mb-2" data-id="element-775">20%</p>
              <p className="text-slate text-sm" data-id="element-776">
                Recurring commission on all referrals
              </p>
            </div>
            <div className="bg-charcoal text-white p-8 rounded-2xl border-4 border-dark transform md:-translate-y-4 shadow-2xl" data-id="element-777">
              <h3 className="font-heading font-black text-2xl text-orange mb-2" data-id="element-778">
                CREATOR
              </h3>
              <p className="text-4xl font-black font-mono mb-2" data-id="element-779">30%</p>
              <p className="text-slate text-sm" data-id="element-780">
                For podcasts, YouTubers, and influencers
              </p>
            </div>
            <div className="bg-charcoal text-white p-8 rounded-2xl border-4 border-dark" data-id="element-781">
              <h3 className="font-heading font-black text-2xl text-blue-400 mb-2" data-id="element-782">
                ORG / SCHOOL
              </h3>
              <p className="text-4xl font-black font-mono mb-2" data-id="element-783">CUSTOM</p>
              <p className="text-slate text-sm" data-id="element-784">
                Special rates for fundraising
              </p>
            </div>
          </div>

          <button className="bg-lime text-charcoal px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-white hover:scale-105 transition-all" data-id="element-785">
            Join Affiliate Program
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-charcoal" data-id="element-786">
        <div className="container mx-auto px-4 max-w-3xl" data-id="element-787">
          <SectionHeading title="FREQUENTLY ASKED QUESTIONS" className="text-center mb-12" data-id="element-788" />

          <div className="space-y-4" data-id="element-789">
            {FAQS.map((faq, i) => <div key={i} className="bg-dark border border-white/10 rounded-2xl overflow-hidden" data-id="element-790">
                <button className="w-full p-6 text-left flex justify-between items-center hover:bg-white/5 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-791">
                  <span className="font-bold text-lg pr-8" data-id="element-792">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="text-lime shrink-0" data-id="element-793" /> : <ChevronDown className="text-slate shrink-0" data-id="element-794" />}
                </button>
                <AnimatePresence data-id="element-795">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-796">
                      <div className="p-6 pt-0 text-slate leading-relaxed border-t border-white/5 mt-2" data-id="element-797">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
};